// Right-hand roster rail, slim variant: draggable support staff as borderless rows with
// role pills, search and A/P assigned-state squares, plus the read-only "Doctors · from
// Sessions" group. In employee rows the rail flips to draggable clinics. No helper copy —
// the affordances carry tooltips instead.
import { useMemo, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Lock, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEFAULT_SESSION_DEFS, sessionLabelFor } from "@/lib/work-plan-sessions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IdentityAvatar } from "./planner-bits";
import { doctorWhereabouts, matchesRolePill, rolePillsFrom, workRoleLabel } from "./work-plan-utils";

function DraggableRow({ dragId, dragData, disabled, className, children, title }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: dragData,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      title={title}
      className={cn(
        "flex items-center gap-2 rounded-md px-1.5 py-[5px] transition-colors duration-120",
        disabled ? "cursor-default" : "cursor-grab touch-none hover:bg-muted",
        isDragging && "opacity-40",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The per-session squares: which sessions the person is already posted into today. One
 * square per configured session, lettered with the label's initial ("A"/"P" for AM/PM,
 * "M"/"A" for Morning/Afternoon) and the full label in the tooltip.
 */
function SessionSquares({ assigned, sessions }) {
  const defs = sessions?.length ? sessions : DEFAULT_SESSION_DEFS;
  return (
    <span className="ml-auto flex shrink-0 gap-1">
      {defs.map((session) => {
        const label = sessionLabelFor(defs, session.key);
        const on = assigned.has(session.key);
        return (
          <span
            key={session.key}
            className={cn(
              "flex size-[15px] items-center justify-center rounded text-[9px] font-black",
              on
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-muted text-muted-foreground/60",
            )}
            title={on ? `Assigned this ${label}` : `Free this ${label}`}
          >
            {label.charAt(0).toUpperCase()}
          </span>
        );
      })}
    </span>
  );
}

export default function RosterRail({
  mode,
  data,
  // Already filtered by the page's clinic-visibility preference — you cannot drag in a
  // clinic whose row the planner is hiding.
  clinics,
  clinicsById,
  roleView,
  role,
  onRoleChange,
  postings,
  doctorSessions,
  // The day's configured sessions (labels drive the A/P squares and the doctor rows).
  sessions,
  canManage,
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const sessionWords = (sessions?.length ? sessions : DEFAULT_SESSION_DEFS)
    .map((session) => sessionLabelFor(sessions, session.key))
    .join(" or ");

  const { supportStaff, doctors } = useMemo(() => {
    const staff = data?.staff || [];
    const matches = (name) => !normalizedQuery || name.toLowerCase().includes(normalizedQuery);
    return {
      supportStaff: staff.filter(
        (person) =>
          !person.isDoctor &&
          matchesRolePill(person, role, roleView) &&
          matches(person.displayName),
      ),
      doctors: staff.filter((person) => person.isDoctor && matches(person.displayName)),
    };
  }, [data, role, roleView, normalizedQuery]);

  const doctorSessionsById = useMemo(() => {
    const map = {};
    for (const item of doctorSessions || []) {
      (map[item.doctorId] ||= []).push(item);
    }
    return map;
  }, [doctorSessions]);

  const assignedSessions = useMemo(() => {
    const map = {};
    for (const posting of postings || []) {
      (map[posting.staffId] ||= new Set()).add(posting.session);
    }
    return map;
  }, [postings]);

  if (mode === "staff") {
    return (
      <aside className="flex h-full min-w-0 flex-col border-l border-border">
        <div className="px-3 pb-1 pt-3">
          <p className="text-[13px] font-bold">Clinics</p>
        </div>
        <ScrollArea className="flex-1" viewportClassName="p-2.5">
          <div className="flex flex-col gap-px">
            {(clinics || data?.clinics || []).map((clinic) => (
              <DraggableRow
                key={clinic.id}
                dragId={`clinic:${clinic.id}`}
                dragData={{ kind: "clinic", id: clinic.id }}
                disabled={!canManage}
                title={canManage ? `Drag ${clinic.name} onto an employee's ${sessionWords} cell` : clinic.name}
              >
                <IdentityAvatar id={clinic.id} name={clinic.shortName || clinic.name} size={22} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs leading-tight">{clinic.name}</span>
                  <span className="block truncate text-[10.5px] leading-tight text-muted-foreground">
                    {clinic.address}
                  </span>
                </span>
              </DraggableRow>
            ))}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  return (
    <aside className="flex h-full min-w-0 flex-col border-l border-border">
      <div className="px-3 pb-1 pt-3">
        <p className="text-[13px] font-bold">Support staff</p>
      </div>
      <div className="flex flex-wrap gap-1 px-3 pt-1">
        {rolePillsFrom(data?.workRoles).map((pill) => (
          <button
            key={pill.id}
            type="button"
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] transition-colors",
              role === pill.id
                ? "bg-foreground font-semibold text-background"
                : "bg-muted text-muted-foreground hover:bg-muted/70",
            )}
            onClick={() => onRoleChange(pill.id)}
          >
            {pill.label}
          </button>
        ))}
      </div>
      <div className="mx-3 mb-1.5 mt-2 flex h-[30px] items-center gap-1.5 rounded-lg border border-border bg-card px-2 dark:border-[oklch(0.36_0.03_264/0.8)]">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find someone…"
          className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/70"
        />
      </div>
      <ScrollArea className="flex-1" viewportClassName="px-2 pb-3">
        <div className="flex flex-col gap-px">
          {supportStaff.map((person) => {
            const onMC = Boolean(person.mcStatus);
            const assigned = assignedSessions[person.id] || new Set();
            return (
              <DraggableRow
                key={person.id}
                dragId={`staff:${person.id}`}
                dragData={{ kind: "staff", id: person.id }}
                disabled={!canManage || onMC}
                title={
                  onMC
                    ? `${person.displayName} — on MC`
                    : `${person.displayName} · ${workRoleLabel(person, roleView)}${canManage ? " — drag onto a session cell" : ""}`
                }
              >
                <IdentityAvatar id={person.id} name={person.displayName} photoUrl={person.avatarUrl} size={22} />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-xs leading-tight",
                    onMC && "text-rose-700 line-through dark:text-rose-300",
                  )}
                >
                  {person.displayName}
                </span>
                {onMC ? (
                  <span className="ml-auto shrink-0 rounded bg-rose-100 px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-rose-600 dark:bg-rose-900/60 dark:text-rose-300">
                    MC
                  </span>
                ) : (
                  <SessionSquares assigned={assigned} sessions={sessions} />
                )}
              </DraggableRow>
            );
          })}
          <div className="mb-1 mt-3 flex items-center gap-2 text-[9.5px] font-bold tracking-widest text-muted-foreground/60">
            <span className="h-px flex-1 bg-border" />
            <span title="Read-only here — doctor sessions are set in the Sessions roster. Shown so you can staff around them.">
              DOCTORS · FROM SESSIONS
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          {doctors.map((doctor) => {
            const where = doctorWhereabouts(doctorSessionsById[doctor.id] || [], clinicsById, sessions);
            const onMC = Boolean(doctor.mcStatus);
            return (
              <div
                key={doctor.id}
                className="flex items-center gap-2 rounded-md px-1.5 py-[5px]"
                title="Managed in Sessions"
              >
                <IdentityAvatar
                  id={doctor.id}
                  name={doctor.displayName}
                  photoUrl={doctor.avatarUrl}
                  size={22}
                  className="opacity-70"
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate text-xs leading-tight text-muted-foreground",
                      onMC && "text-rose-700 line-through dark:text-rose-300",
                    )}
                  >
                    {doctor.displayName}
                  </span>
                  <span className="block truncate text-[10.5px] leading-tight text-muted-foreground/70">
                    {where || "No session today"}
                  </span>
                </span>
                {onMC ? (
                  <span className="shrink-0 rounded bg-rose-100 px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-rose-600 dark:bg-rose-900/60 dark:text-rose-300">
                    MC
                  </span>
                ) : (
                  <Lock className="size-3.5 shrink-0 text-muted-foreground/40" />
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </aside>
  );
}
